/**
 * Interface for cancellation tokens that can stop staged events from executing.
 * Provides mechanisms to check cancellation state and reset it.
 */
/**
 * Cancellation token for debounced events.
 * Allows cancellation and reset of a pending debounced event.
 */
export interface CancellationToken {
  /** Whether the operation has been cancelled */
  cancelled: boolean;

  /** Resets the cancellation state back to false */
  reset: () => void;

  /** Cancels the operation */
  cancel: () => void;
}

/**
 * Default implementation of a cancellation token for debounced events.
 */
export class DefaultCancellationToken implements CancellationToken {
  private _cancelled = false;
  get cancelled(): boolean {
    return this._cancelled;
  }
  cancel() {
    this._cancelled = true;
  }
  reset() {
    this._cancelled = false;
  }
}
/**
 * Calculates the elapsed time in milliseconds since the provided date.
 * @param startTime The reference date to measure elapsed time from
 * @returns Number of milliseconds that have elapsed since the provided date
 */
export function getElapsedMilliseconds(startTime: Date): number {
  return Date.now() - startTime.getTime();
}

/**
 * Configuration options for a staged event.
 */
/**
 * Options for configuring a debounced event.
 */
export interface DebouncedEventOptions {
  /**
   * The main event function to execute after the threshold period.
   * This will only execute once the threshold time has passed.
   */
  event: () => void | Promise<void>;

  /**
   * Time in milliseconds to wait before executing the main event function.
   * Additional triggers during this period will not restart the timer.
   * @default 15000 (15 seconds)
   */
  threshold?: number;

  /**
   * Optional title for the staged event, used in logging.
   */
  title?: string;

  /**
   * Called after the main event completes or is cancelled.
   * Used for cleanup operations.
   */
  eventFinally?: () => void;

  /**
   * Called when the staged event is first triggered.
   * This begins the threshold countdown.
   */
  eventTriggered?: () => void | Promise<void>;

  /**
   * Called each time the staged event is actioned (triggered again while waiting).
   * This doesn't restart the timer but can be used to update UI or collect additional data.
   */
  eventActioned?: () => void | Promise<void>;

  /**
   * Optional token that can be used to cancel the staged event.
   */
  /**
   * Optional token that can be used to cancel the debounced event.
   */
  cancellationToken?: CancellationToken;

  /**
   * Interval in milliseconds between checks for threshold completion.
   * @default 1000 (1 second)
   */
  testInterval?: number;

  /**
   * Maximum number of times this event can be actioned before forcing execution.
   * If specified, the event will execute immediately once actionCount reaches this value,
   * regardless of threshold time remaining.
   * @default undefined (no limit)
   */
  maxActionCount?: number;

  /**
   * Policy for handling new triggers while executing.
   * - 'queue': Queue the event to be processed after current execution
   * - 'ignore': Ignore new triggers during execution
   * - 'restart': Cancel current execution and restart with new trigger
   * @default 'queue'
   */
  executionPolicy?: 'queue' | 'ignore' | 'restart';
}

/** Default debounce duration in milliseconds (15 seconds) before executing the main event */
export const defaultDebounceDuration = 15000;

/** Default interval in milliseconds (1 second) between checks for threshold completion */
export const defaultTestInterval = 1000;

/**
 * Creates a staged event function that implements a delayed execution pattern.
 *
 * Staged execution should only be used when:
 * 1. Receiving the trigger is sufficient and specific data is not important
 * 2. Multiple rapid triggers should be coalesced into a single action
 * 3. A delay between trigger and execution is acceptable or desired
 *
 * Common use cases:
 * - Debouncing notifications or updates
 * - Collecting multiple events before performing an expensive operation
 * - Providing users time to cancel an action before it completes
 *
 * @param options Configuration options for the staged event
 * @returns A function that when called triggers the staged event
 */
/**
 * Creates a debounced event function that implements a delayed execution pattern.
 *
 * Debounced execution should only be used when:
 * 1. Receiving the trigger is sufficient and specific data is not important
 * 2. Multiple rapid triggers should be coalesced into a single action
 * 3. A delay between trigger and execution is acceptable or desired
 *
 * @param options Configuration options for the debounced event
 * @returns A function that when called triggers the debounced event
 */
export function createDebouncedEvent(
  options: DebouncedEventOptions
): () => void {
  const intervalMs = options.testInterval ?? defaultTestInterval;
  const executionPolicy = options.executionPolicy ?? 'queue';
  const threshold = options.threshold ?? defaultDebounceDuration;

  // State variables for this staged event instance
  let timer: ReturnType<typeof setTimeout> | undefined = undefined;
  let triggered: Date | undefined = undefined;
  let actioned: Date | undefined = undefined;
  let actionCount = 0;
  let executing = false;
  let pendingRestart = false;

  /**
   * Resets the state of the staged event
   */
  const reset = () => {
    clearTimeout(timer);
    timer = undefined;
    triggered = undefined;
    actionCount = 0;
    actioned = undefined;
  };

  /**
   * The interval check function that determines if the threshold has been reached
   * and executes the main event when appropriate
   */
  const intervalEvent = async () => {
    try {
      // Check for cancellation
      if (options.cancellationToken?.cancelled) {
        reset();
        options.cancellationToken?.reset();
        options.eventFinally?.();
        return;
      }

      // Get elapsed time since triggered
      const elapsedMs = getElapsedMilliseconds(triggered!);

      // Check if we should execute (either threshold reached or max action count reached)
      const thresholdReached = elapsedMs >= threshold;
      const maxActionsReached =
        options.maxActionCount !== undefined &&
        actionCount >= options.maxActionCount;

      if (thresholdReached || maxActionsReached) {
        try {
          executing = true;

          // Log execution reason
          if (maxActionsReached) {
            console.log(
              `Staged Event '${options.title ?? 'no name'}' executed: max action count ${actionCount} reached`
            );
          } else {
            console.log(
              `Staged Event '${options.title ?? 'no name'}' executed: threshold of ${threshold}ms reached after ${elapsedMs}ms`
            );
          }

          // Execute the main event
          await options.event();
        } finally {
          executing = false;
          options.eventFinally?.();
          reset();

          // Handle pending restart based on execution policy
          if (pendingRestart) {
            pendingRestart = false;
            startProcess();
          }
        }
        return;
      }

      // Schedule next check
      timer = setTimeout(intervalEvent, intervalMs);
    } catch (error) {
      console.error(
        `Error in staged event '${options.title ?? 'no name'}':`,
        error
      );
      options.eventFinally?.();
      reset();
    }
  };
  /**
   * The function returned to the caller that triggers the staged event
   */
  return () => {
    try {
      if (!triggered) {
        // First trigger - start the process
        startProcess();
      } else {
        // Subsequent triggers - handle according to policy
        if (executing) {
          switch (executionPolicy) {
            case 'ignore':
              console.log(
                `Debounced Event '${options.title ?? 'no name'}' ignored trigger during execution (policy: ignore)`
              );
              return;

            case 'restart':
              console.log(
                `Debounced Event '${options.title ?? 'no name'}' will restart after execution (policy: restart)`
              );
              pendingRestart = true;
              break;

            case 'queue':
            default:
              console.log(
                `Debounced Event '${options.title ?? 'no name'}' queued trigger for after execution (policy: queue)`
              );
              pendingRestart = true;
              break;
          }
        }

        // Increment action count and record timestamp
        actionCount++;
        actioned = new Date();

        // Call the actioned callback
        try {
          void options.eventActioned?.();
        } catch (error) {
          console.error(
            `Error in eventActioned callback for '${options.title ?? 'no name'}':`,
            error
          );
        }

        // Log the action
        console.log(
          `Debounced Event '${options.title ?? 'no name'}' actioned ${actionCount} since ${triggered.toLocaleTimeString()}, at ${actioned.toLocaleTimeString()}`
        );
      }
    } catch (error) {
      console.error(
        `Error triggering staged event '${options.title ?? 'no name'}':`,
        error
      );
    }
  };

  /**
   * Starts the staged event process by initializing state and scheduling the first check
   */
  function startProcess() {
    try {
      // Initialize/reset state
      executing = false;
      pendingRestart = false;
      options.cancellationToken?.reset();
      triggered = new Date();
      actionCount = 1; // Start at 1 for the initial trigger
      actioned = triggered;

      // Call event callbacks
      try {
        void options.eventTriggered?.();
      } catch (error) {
        console.error(
          `Error in eventTriggered callback for '${options.title ?? 'no name'}':`,
          error
        );
      }

      try {
        void options.eventActioned?.();
      } catch (error) {
        console.error(
          `Error in eventActioned callback for '${options.title ?? 'no name'}':`,
          error
        );
      }

      // Schedule the first check
      timer = setTimeout(intervalEvent, intervalMs);

      // Log the start
      console.log(
        `Debounced Event '${options.title ?? 'no name'}' started at ${triggered.toLocaleTimeString()}, ` +
          `will execute in ${threshold}ms or after ${options.maxActionCount ?? '∞'} actions`
      );
    } catch (error) {
      console.error(
        `Error starting staged event '${options.title ?? 'no name'}':`,
        error
      );
      options.eventFinally?.();
    }
  }
}
